import { isValue, type Suit } from "../types/card";
import type { EngineEvent, GameState, StackIndex } from "../types/game";
import { STACK_INDICES } from "../types/game";
import { matchDistance } from "./matchDistance";

/**
 * "What could that card have been worth?" — the Match guess a player didn't make.
 *
 * ADVISORY ONLY. Nothing here changes a rule or a score; it reads a move that has
 * already been resolved and reports the better outcome that was sitting on the
 * table. The Go server has no counterpart and needs none — this is coaching, and
 * it runs on the client that's doing the coaching.
 *
 * The counterfactual is honest because the drawn card does not depend on the
 * move: every path through applyMove starts with drawTop(player), so guessing
 * Match on a different stack would have turned over the very same card. That is
 * only true of the FIRST card of a turn, which is why `analyzeMissedMatch` reads
 * the first card-played event and ignores everything the turn went on to draw.
 *
 * Exists because new players almost never guess Match: the opportunities are far
 * more frequent than they look, and nothing in the game ever pointed that out.
 */

/** How good a Match guess on a given stack would have been. */
export type MatchOpportunity = "exact" | "off-by-one";

/** One stack a Match guess would have scored on. */
export interface MatchChance {
  stackIndex: StackIndex;
  /**
   * The number that stack was showing. Equal to the drawn card for an exact,
   * one either side of it for an off-by-one — carried explicitly because the
   * UI highlights the number as it appears on the table, and "one either side"
   * doesn't say which side.
   */
  value: number;
  /** That card's suit: a painted deck prints its numeral at a per-suit height. */
  suit: Suit;
  tier: MatchOpportunity;
}

export interface MissedMatchAnalysis {
  /** Face value of the card the player turned over. */
  cardValue: number;
  cardSuit: Suit;
  /** Where the guess actually landed. */
  playedStackIndex: StackIndex;
  /** Every stack that was worth a Match guess, best tier first. */
  chances: MatchChance[];
  /**
   * The best result the player passed up, or null when they took it (or when
   * nothing was on offer). At most one per turn: a board holding both an exact
   * and an off-by-one counts once, as the exact — the player only had one card.
   */
  missed: MatchOpportunity | null;
}

/**
 * Reads one resolved move. Returns null when the turn had no Match opportunity
 * to speak of: a wild was played, no card was played at all, or every stack was
 * empty/wild-topped (all of which are free placements, where Match isn't legal).
 *
 * `stateBefore` must be the board as it was BEFORE the move was applied.
 */
export function analyzeMissedMatch(stateBefore: GameState, events: EngineEvent[], playerId: string): MissedMatchAnalysis | null {
  // The first card-played of the turn is the one the player committed the guess
  // to. Later events (duplicates, chain and Five Out bonus cards) are downstream
  // of that choice and were never available to guess on.
  const played = events.find((ev) => ev.type === "card-played" && ev.playerId === playerId);
  if (!played || played.type !== "card-played") return null;
  if (!isValue(played.card)) return null; // a wild has no value to match against

  const cardValue = played.card.value;
  const chances: MatchChance[] = [];

  for (const idx of STACK_INDICES) {
    const top = stateBefore.stacks[idx].cards[0];
    // Empty and wild-topped stacks take a free placement, not a guess, so there
    // was never a Match to make on them.
    if (!top || !isValue(top)) continue;
    const diff = matchDistance(top.value, cardValue);
    if (diff === 0) chances.push({ stackIndex: idx, value: top.value, suit: top.suit, tier: "exact" });
    else if (diff === 1) chances.push({ stackIndex: idx, value: top.value, suit: top.suit, tier: "off-by-one" });
  }

  if (chances.length === 0) return null;
  chances.sort((a, b) => (a.tier === b.tier ? a.stackIndex - b.stackIndex : a.tier === "exact" ? -1 : 1));

  // What the turn actually produced. A Five Out means nothing was missed; an
  // off-by-one still misses an exact that was available elsewhere.
  const gotExact = events.some((ev) => ev.type === "five-out" && ev.playerId === playerId);
  const gotOffByOne = events.some((ev) => ev.type === "match-off-by-one" && ev.playerId === playerId);
  const hadExact = chances.some((c) => c.tier === "exact");

  let missed: MatchOpportunity | null = null;
  if (hadExact) {
    if (!gotExact) missed = "exact";
  } else if (!gotExact && !gotOffByOne) {
    missed = "off-by-one";
  }

  return { cardValue, cardSuit: played.card.suit, playedStackIndex: played.stackIndex, chances, missed };
}
